import { useCallback } from "react";
import { Collapse, Typography } from "@mui/material";
import DoctorCard from "../../../components/dashboard/doctorCard/DoctorCard";
import InsuranceCard from "../../../components/dashboard/InsuranceCard/InsuranceCard";
import AverageLengthOfStayCard from "./averageLengthOfStayCard/AverageLengthOfStayCard";
import { ADMISSION_CARDS_LABELS, ADMISSION_CARDS_TYPE } from "../../../types/admission.type";
import { PAGE_TYPE } from "../../../types/pages.type";
import DayCardList from "../../hospital/components/dayCardList/DayCardList";
import DxCardList from "./dxCardList/DxCardList";
import FloorCardList from "../../hospital/components/floorCardList/FloorCardList";
import DynamicDataCardList from "../../hospital/components/DynamicDataCardList/DynamicDataCardList";
import HospitalList from "./hospitalList/HospitalList";
import { findHospitalDynamicCardByKey, HeaderFilterCardShell, PanelWrap } from "../../hospital/components/HospitalHeaderCardFilterPanel";

function AdmissionHeaderDynamicFilterBody({
	ele,
	accessor,
	cardFilter,
	filterListData,
	filterListDataComparison,
	handleToggle,
	handleToggleAll,
	filter,
	filterComparison,
	loadingBoth,
	dbData,
	dbDataComparison,
	percentageAgainst,
	averageCensusComparison,
	projectionDays,
	getPriorityNumber,
	transferType,
	lockedTotalBy,
	filterTotal,
	lockFilterTotalState,
	lockFilterTotalStateComparison,
	reversedColorAdmission,
}) {
	const cardSelectedItems = cardFilter?.[accessor] ?? [];
	const cardID = filterListData?.[accessor]?.[0]?.id;
	const isSelectedTotalCard = cardSelectedItems.indexOf(cardID) !== -1;
	const isCustomTab = Boolean(ele?.isCustomTab);
	const question = ele?.question;

	const handleCardClick = useCallback(
		async (_titleParam = null, uniqueKey = null) => {
			const compositeKey = uniqueKey && accessor ? `${accessor}__${uniqueKey}` : null;
			const comparisonItem = filterListDataComparison?.[accessor]?.[0];
			await handleToggle({
				item: filterListData?.[accessor]?.[0],
				itemComparison: comparisonItem,
				type: accessor,
				isChecked: isSelectedTotalCard,
				cardTitle: ele.title || ele.label || ele.tableLabel,
				question: {
					isCustom: true,
					customQuestionInputType: question?.customQuestionInputType,
					isCustomTab: false,
					cardTitle: ele.title || ele.label || ele.tableLabel,
					customTab: ele.type,
					accessor,
					compositeKey,
					uniqueKey,
				},
				isCustomTab: false,
			});
		},
		[accessor, ele, filterListData, filterListDataComparison, handleToggle, isSelectedTotalCard, question]
	);

	const admissionCompareAgainst =
		lockFilterTotalState?.isLocked ? lockFilterTotalState?.lockedFilterTotal : dbData?.bedCapacity;
	const admissionCompareAgainstComparison =
		lockFilterTotalStateComparison?.isLocked
			? lockFilterTotalStateComparison?.lockedFilterTotal
			: dbDataComparison?.bedCapacity;

	return (
		<DynamicDataCardList
			compactForHeaderPopover
			disableInteractions={false}
			totalTransfers={dbData?.total}
			totalTransfersComparison={dbDataComparison?.total}
			filterListData={filterListData}
			filterListDataComparison={filterListDataComparison}
			question={ele}
			cardTitle={ele.title || ele.label || ele.tableLabel}
			data={filterListData?.[accessor] ?? []}
			dataComparison={filterListDataComparison?.[accessor] ?? []}
			handleToggle={handleToggle}
			handleToggleAll={handleToggleAll}
			selectedItem={cardFilter?.[accessor]}
			spacialSelectedItem={cardFilter?.[`${accessor}_spacial`]}
			type={accessor}
			filter={filter}
			loading={loadingBoth}
			filterComparison={filterComparison}
			page={PAGE_TYPE.ADMISSION}
			averageCensus={percentageAgainst}
			projectionDays={projectionDays}
			isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
			priorityNumber={getPriorityNumber(accessor)}
			transferType={transferType}
			lockedTotalBy={lockedTotalBy}
			isCustomTab={isCustomTab}
			filterTotal={filterTotal}
			handleCardClick={handleCardClick}
			isSelectedTotalCard={isSelectedTotalCard}
			cardFilter={cardFilter}
			isDisabled={false}
			admissionCompareAgainst={admissionCompareAgainst}
			admissionCompareAgainstComparison={admissionCompareAgainstComparison}
			reverseColorsAdmissionPage={reversedColorAdmission}
			averageCensusComparison={averageCensusComparison}
		/>
	);
}

/**
 * Interactive filter popovers for Admission dashboard header chips (same handlers/data as main cards).
 */
export default function AdmissionHeaderCardFilterPanel({
	dataKey,
	handleToggle,
	handleToggleAll,
	filterListData,
	filterListDataComparison,
	cardFilter,
	filter,
	filterComparison,
	loading,
	loadingComparison,
	projectionDays,
	filterTotal,
	dbData,
	dbDataComparison,
	transferType,
	lockedTotalBy,
	getPriorityNumber,
	comparingAgainstDifferentFacility,
	dynamicCards = [],
	admissionDynamicTabDataByAccessor = {},
	percentageAgainst,
	reversedColorAdmission,
	lockFilterTotalState,
	lockFilterTotalStateComparison,
	headerFilterPopoverOpen,
}) {
	const loadingBoth = loading || loadingComparison;
	const admissionCompareAgainst =
		lockFilterTotalState?.isLocked ? lockFilterTotalState?.lockedFilterTotal : dbData?.bedCapacity;
	const admissionCompareAgainstComparison =
		lockFilterTotalStateComparison?.isLocked
			? lockFilterTotalStateComparison?.lockedFilterTotal
			: dbDataComparison?.bedCapacity;

	const panelContent = (() => {
		switch (dataKey) {
		case ADMISSION_CARDS_TYPE.DOCTOR_DATA:
			return (
				<PanelWrap title={ADMISSION_CARDS_LABELS.doctorData}>
					<DoctorCard
						cardTitle={ADMISSION_CARDS_LABELS.doctorData}
						data={filterListData?.doctorData ?? []}
						comparisonData={filterListDataComparison?.doctorData}
						totalAdmissions={dbData.total}
						totalAdmissionsComparison={dbDataComparison?.total}
						minimizable={true}
						admissionCompareAgainst={admissionCompareAgainst}
						admissionCompareAgainstComparison={admissionCompareAgainstComparison}
						handleToggle={handleToggle}
						handleToggleAll={handleToggleAll}
						selectedItem={cardFilter?.doctorData ?? []}
						type={ADMISSION_CARDS_TYPE.DOCTOR_DATA}
						page={PAGE_TYPE.ADMISSION}
						filter={filter}
						loading={loadingBoth}
						reverseColorsAdmissionPage={reversedColorAdmission}
						filterComparison={filterComparison}
						comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
						projectionDays={projectionDays}
						averageCensus={percentageAgainst}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={Boolean(filterListData?.isComparingAgainstAvgCensus)}
						priorityNumber={getPriorityNumber(ADMISSION_CARDS_TYPE.DOCTOR_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
						isMobile={false}
						forHeaderPopover
					/>
				</PanelWrap>
			);

		case ADMISSION_CARDS_TYPE.DAYS_DATA:
			return (
				<PanelWrap title={ADMISSION_CARDS_LABELS.daysData}>
					<DayCardList
						cardTitle={ADMISSION_CARDS_LABELS.daysData}
						dbData={dbData}
						dbDataComparison={dbDataComparison}
						data={filterListData.daysData}
						dataComparison={filterListDataComparison?.daysData}
						totalAdmissions={dbData.total}
						totalAdmissionsComparison={dbDataComparison?.total}
						handleToggle={handleToggle}
						filter={filter}
						filterComparison={filterComparison}
						admissionCompareAgainst={admissionCompareAgainst}
						admissionCompareAgainstComparison={admissionCompareAgainstComparison}
						selectedItem={cardFilter.daysData}
						type={ADMISSION_CARDS_TYPE.DAYS_DATA}
						page={PAGE_TYPE.ADMISSION}
						loading={loadingBoth}
						reverseColorsAdmissionPage={reversedColorAdmission}
						comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						priorityNumber={getPriorityNumber(ADMISSION_CARDS_TYPE.DAYS_DATA)}
						disableInteractions={false}
					/>
				</PanelWrap>
			);

		case ADMISSION_CARDS_TYPE.DX_DATA:
			return (
				<PanelWrap title={ADMISSION_CARDS_LABELS.dxData}>
					<DxCardList
						cardTitle="Per Diagnosis"
						data={filterListData.dxData}
						dataComparison={filterListDataComparison?.dxData}
						filter={filter}
						filterComparison={filterComparison}
						admissionCompareAgainst={admissionCompareAgainst}
						admissionCompareAgainstComparison={admissionCompareAgainstComparison}
						handleToggle={handleToggle}
						selectedItem={cardFilter.dxData}
						type={ADMISSION_CARDS_TYPE.DX_DATA}
						page={PAGE_TYPE.ADMISSION}
						loading={loadingBoth}
						reverseColorsAdmissionPage={reversedColorAdmission}
						comparingAgainstDifferentFacility={false}
						projectionDays={projectionDays}
						isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
						priorityNumber={getPriorityNumber(ADMISSION_CARDS_TYPE.DX_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
					/>
				</PanelWrap>
			);

		case ADMISSION_CARDS_TYPE.INSURANCE_DATA:
			return (
				<HeaderFilterCardShell
					title={ADMISSION_CARDS_LABELS.insuranceData}
					bodySx={{ p: 0, pt: 0.5, overflow: "visible" }}
				>
					<InsuranceCard
						dbData={dbData}
						dbDataComparison={dbDataComparison}
						cardTitle={ADMISSION_CARDS_LABELS.insuranceData}
						data={filterListData.insuranceData}
						dataComparison={filterListDataComparison?.insuranceData}
						totalAdmissions={dbData.total}
						totalAdmissionsComparison={dbDataComparison?.total}
						handleToggle={handleToggle}
						handleToggleAll={handleToggleAll}
						selectedItem={cardFilter.insuranceData}
						type={ADMISSION_CARDS_TYPE.INSURANCE_DATA}
						page={PAGE_TYPE.ADMISSION}
						filter={filter}
						admissionCompareAgainst={admissionCompareAgainst}
						admissionCompareAgainstComparison={admissionCompareAgainstComparison}
						filterComparison={filterComparison}
						reverseColorsAdmissionPage={reversedColorAdmission}
						comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
						loading={loadingBoth}
						projectionDays={projectionDays}
						isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
						priorityNumber={getPriorityNumber(ADMISSION_CARDS_TYPE.INSURANCE_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						headerFilterPopover
						hideProgressBar
						disableInteractions={false}
						isMobile={false}
					/>
				</HeaderFilterCardShell>
			);

		case ADMISSION_CARDS_TYPE.FLOORS_DATA:
			return (
				<PanelWrap title={ADMISSION_CARDS_LABELS.floorsData}>
					<FloorCardList
						cardTitle={ADMISSION_CARDS_LABELS.floorsData}
						data={filterListData.floorsData}
						dataComparison={filterListDataComparison?.floorsData}
						totalAdmissions={dbData.total}
						totalAdmissionsComparison={dbDataComparison?.total}
						handleToggle={handleToggle}
						filter={filter}
						filterComparison={filterComparison}
						selectedItem={cardFilter.floorsData}
						type={ADMISSION_CARDS_TYPE.FLOORS_DATA}
						page={PAGE_TYPE.ADMISSION}
						admissionCompareAgainst={admissionCompareAgainst}
						admissionCompareAgainstComparison={admissionCompareAgainstComparison}
						reverseColorsAdmissionPage={reversedColorAdmission}
						comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
						loading={loadingBoth}
						projectionDays={projectionDays}
						isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
						priorityNumber={getPriorityNumber(ADMISSION_CARDS_TYPE.FLOORS_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
					/>
				</PanelWrap>
			);

		case ADMISSION_CARDS_TYPE.HOSPITAL_DATA:
			return (
				<PanelWrap title={ADMISSION_CARDS_LABELS.hospitalData}>
					<HospitalList
						dbData={dbData}
						dbDataComparison={dbDataComparison}
						cardTitle="Per Hospital"
						total={dbData.total}
						totalComparison={dbDataComparison?.total}
						data={filterListData.hospitalData}
						dataComparison={filterListDataComparison?.hospitalData}
						filter={filter}
						filterComparison={filterComparison}
						handleToggle={handleToggle}
						selectedItem={cardFilter.hospitalData}
						type={ADMISSION_CARDS_TYPE.HOSPITAL_DATA}
						page={PAGE_TYPE.ADMISSION}
						admissionCompareAgainst={admissionCompareAgainst}
						admissionCompareAgainstComparison={admissionCompareAgainstComparison}
						loading={loadingBoth}
						reverseColorsAdmissionPage={reversedColorAdmission}
						comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						averageCensus={percentageAgainst}
						priorityData={cardFilter.mainPriorityData}
						priorityNumber={getPriorityNumber(ADMISSION_CARDS_TYPE.HOSPITAL_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
						disableInteractions={false}
					/>
				</PanelWrap>
			);

		case ADMISSION_CARDS_TYPE.AVERAGE_STAY_DAYS_DATA:
			return (
				<PanelWrap title={ADMISSION_CARDS_LABELS.averageStayDaysData}>
					<AverageLengthOfStayCard
						data={filterListData.averageStayDaysData}
						comparisonData={filterListDataComparison?.averageStayDaysData}
						loading={loading}
						loadingComparison={loadingComparison}
						filter={filter}
						filterComparison={filterComparison}
						comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
						admissionCompareAgainst={admissionCompareAgainst}
						admissionCompareAgainstComparison={admissionCompareAgainstComparison}
						reverseColorsAdmissionPage={reversedColorAdmission}
						isMobile={false}
						page={PAGE_TYPE.ADMISSION}
					/>
				</PanelWrap>
			);

		default: {
			const dynamicEle =
				admissionDynamicTabDataByAccessor[dataKey] || findHospitalDynamicCardByKey(dataKey, dynamicCards);
			if (dynamicEle) {
				const accessor = dynamicEle.accessor || dataKey;
				const dynamicTitle =
					dynamicEle.title || dynamicEle.label || dynamicEle.tableLabel || String(dataKey);
				return (
					<PanelWrap title={dynamicTitle}>
						<AdmissionHeaderDynamicFilterBody
							ele={dynamicEle}
							accessor={accessor}
							cardFilter={cardFilter}
							filterListData={filterListData}
							filterListDataComparison={filterListDataComparison}
							handleToggle={handleToggle}
							handleToggleAll={handleToggleAll}
							filter={filter}
							filterComparison={filterComparison}
							loadingBoth={loadingBoth}
							dbData={dbData}
							dbDataComparison={dbDataComparison}
							percentageAgainst={percentageAgainst}
							averageCensusComparison={dbDataComparison?.censusAverage}
							projectionDays={projectionDays}
							getPriorityNumber={getPriorityNumber}
							transferType={transferType}
							lockedTotalBy={lockedTotalBy}
							filterTotal={filterTotal}
							lockFilterTotalState={lockFilterTotalState}
							lockFilterTotalStateComparison={lockFilterTotalStateComparison}
							reversedColorAdmission={reversedColorAdmission}
						/>
					</PanelWrap>
				);
			}
			return (
				<PanelWrap title="Filter">
					<Typography variant="body2" sx={{ p: 1, color: "text.secondary", fontSize: 12 }}>
						This filter is not available in the header preview.
					</Typography>
				</PanelWrap>
			);
		}
	}
	})();

	if (headerFilterPopoverOpen === undefined) {
		return panelContent;
	}

	return (
		<Collapse orientation="vertical" in={headerFilterPopoverOpen} timeout={1000} unmountOnExit={false}>
			{panelContent}
		</Collapse>
	);
}
